import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, Mail, Phone, Inbox } from "lucide-react";
import { Reveal } from "../components/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("ofc_admin_token") || "");
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Panell — Orbis Forum Català";
  }, []);

  const fetchMessages = async (t) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/contact/messages`, {
        headers: { "X-Admin-Token": t },
      });
      setMessages(data);
      setToken(t);
      localStorage.setItem("ofc_admin_token", t);
    } catch (err) {
      setError("Token incorrecte o error de connexió.");
      setToken("");
      localStorage.removeItem("ofc_admin_token");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchMessages(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 pt-20">
        <Reveal className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8">
            <Lock size={18} strokeWidth={1.5} className="text-ofc-gold" />
            <h1 className="font-serif-display text-2xl">Panell reservat</h1>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              fetchMessages(input);
            }}
            className="space-y-6"
          >
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="admin-token-input"
              placeholder="Token d'accés"
              className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-3 text-white placeholder-white/25 focus:outline-none focus:border-ofc-gold transition-colors"
            />
            {error && <p className="text-sm text-red-400">{error}</p>}
            <button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading}
              className="w-full bg-ofc-gold text-ofc-black py-3 text-xs uppercase tracking-[0.2em] hover:bg-ofc-goldhover transition-colors disabled:opacity-50"
            >
              {loading ? "Verificant..." : "Accedir"}
            </button>
          </form>
        </Reveal>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-3">
            <Inbox size={20} strokeWidth={1.5} className="text-ofc-gold" />
            <h1 className="font-serif-display text-3xl">Missatges de contacte</h1>
            <span className="text-sm text-white/40">({messages.length})</span>
          </div>
          <button
            onClick={() => {
              localStorage.removeItem("ofc_admin_token");
              setToken("");
            }}
            className="text-xs uppercase tracking-[0.2em] text-white/50 hover:text-ofc-gold transition-colors"
          >
            Sortir
          </button>
        </div>

        {messages.length === 0 ? (
          <p className="text-white/50 font-light">Encara no hi ha missatges.</p>
        ) : (
          <div className="space-y-px bg-white/5" data-testid="admin-messages">
            {messages.map((m) => (
              <div key={m.id} className="bg-ofc-black p-6 md:p-8">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="font-serif-display text-xl">{m.name}</h3>
                  <span className="text-xs text-white/40">
                    {new Date(m.created_at).toLocaleString("ca-ES")}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-8 gap-y-2 mt-3 text-sm text-white/60">
                  <a href={`mailto:${m.email}`} className="inline-flex items-center gap-2 hover:text-ofc-gold">
                    <Mail size={14} strokeWidth={1.5} /> {m.email}
                  </a>
                  {m.phone && (
                    <span className="inline-flex items-center gap-2">
                      <Phone size={14} strokeWidth={1.5} /> {m.phone}
                    </span>
                  )}
                </div>
                <p className="mt-4 text-white/70 font-light leading-relaxed whitespace-pre-wrap">{m.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
